import React, { useState } from "react";
import BillingTable from "../BillingTable";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Search,
  Download,
  Filter,
  FileText,
  Calendar,
  FilterX,
} from "lucide-react";
import { Permissions } from "../../../../../permissions";

const CustomerBillingTab = ({ customerId }) => {
  const [timeFilter, setTimeFilter] = useState("month");
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    status: "",
    dateRange: "",
    sortBy: "date_desc",
  });

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearAllFilters = () => {
    setTimeFilter("month");
    setSearchQuery("");
    setFilters({
      status: "",
      dateRange: "",
      sortBy: "date_desc",
    });
  };

  const canDownload = Permissions.find(p =>
    p.includes("download_customerBilling"),
  );

  return (
    <div className="space-y-6">
      {/* Filters and Search Section */}
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between gap-4 mb-6">
          <div className="flex-1">
            <TextField
              placeholder="Search by invoice no., amount..."
              variant="outlined"
              fullWidth
              size="small"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} className="text-gray-500" />
                  </InputAdornment>
                ),
                sx: {
                  fontSize: "14px",
                  borderRadius: "0.375rem",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E5E7EB",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#5652B0",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#5652B0",
                  },
                },
              }}
              className="font-outfit"
            />
          </div>

          <div className="flex items-center gap-4 flex-wrap">
            {(searchQuery ||
              timeFilter !== "month" ||
              Object.values(filters).some(
                value => value !== "" && value !== "date_desc",
              )) && (
              <button
                onClick={clearAllFilters}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 
                         hover:bg-gray-100 rounded-lg transition-colors">
                <FilterX size={16} />
                Clear Filters
              </button>
            )}
            {canDownload && (
              <button className="flex items-center gap-2 px-4 py-2 rounded-md text-gray-700 border border-gray-300 hover:bg-gray-50 font-outfit">
                <Download size={16} />
                <span className="text-sm">Export</span>
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          {/* Time Range Filter */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={timeFilter}
              label="Time Period"
              onChange={e => setTimeFilter(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#5652B0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#5652B0",
                },
                fontFamily: "Outfit",
              }}>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="quarter">Last Quarter</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
              <MenuItem value="all">All Time</MenuItem>
            </Select>
          </FormControl>

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={e => handleFilterChange("status", e.target.value)}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#5652B0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#5652B0",
                },
                fontFamily: "Outfit",
              }}>
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </Select>
          </FormControl>

          {/* Sort By Filter */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={filters.sortBy}
              label="Sort By"
              onChange={e => handleFilterChange("sortBy", e.target.value)}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#5652B0",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#5652B0",
                },
                fontFamily: "Outfit",
              }}>
              <MenuItem value="date_desc">Date (Newest First)</MenuItem>
              <MenuItem value="date_asc">Date (Oldest First)</MenuItem>
              <MenuItem value="amount_desc">Amount (High-Low)</MenuItem>
              <MenuItem value="amount_asc">Amount (Low-High)</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      {/* Billing Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-outfit">Total Billing</p>
              <h3 className="text-2xl font-semibold text-gray-800 font-outfit mt-1">
                ₹ 2,45,000
              </h3>
            </div>
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <FileText className="text-[#5652B0]" size={20} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-outfit">
                Outstanding Amount
              </p>
              <h3 className="text-2xl font-semibold text-gray-800 font-outfit mt-1">
                ₹ 35,000
              </h3>
            </div>
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
              <Calendar className="text-amber-600" size={20} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-outfit">Payment Due</p>
              <h3 className="text-2xl font-semibold text-gray-800 font-outfit mt-1">
                ₹ 15,000
              </h3>
              <p className="text-xs text-red-500 font-outfit mt-1">
                Due on 25th Jul 2024
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <Calendar className="text-red-600" size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Billing Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 font-outfit">
            Billing History
          </h3>
        </div>
        <div className="p-6">
          <BillingTable />
        </div>
      </div>
    </div>
  );
};

export default CustomerBillingTab;
